import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import { ORGANIGRAMA_ROLE_COLORS } from "./organigramaColors";
import { useTranslation } from "react-i18next";

const COLORS = ORGANIGRAMA_ROLE_COLORS;

const MetricBar = ({ value, color }) => (
  <Box
    sx={{
      mt: 0.75,
      pt: 0.5,
      borderTop: "1px solid",
      borderColor: alpha(color, 0.25),
    }}
  >
    <Typography
      variant="caption"
      sx={{ fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}
    >
      {value}
    </Typography>
    <Box
      sx={{
        mt: 0.35,
        height: 4,
        borderRadius: 1,
        bgcolor: alpha(color, 0.15),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: value === "14.900" ? "92%" : "55%",
          borderRadius: 1,
          bgcolor: color,
        }}
      />
    </Box>
  </Box>
);

const PersonBox = ({
  name,
  role,
  metric,
  color = "#333",
  borderColor,
  bgcolor,
  sx = {},
  compact = false,
  muted = false,
  large = false,
}) => (
  <Paper
    elevation={0}
    sx={{
      px: large ? 2 : compact ? 1 : 1.5,
      py: large ? 1.35 : compact ? 0.6 : 1,
      borderRadius: 2.5,
      border: large ? "3px solid" : "2px solid",
      borderColor: borderColor || color,
      bgcolor: bgcolor || alpha(color, muted ? 0.04 : 0.1),
      textAlign: "center",
      minWidth: compact
        ? { xs: 0, sm: 64 }
        : large
          ? { xs: 0, sm: 130 }
          : { xs: 0, sm: 96 },
      width: { xs: "100%", sm: "auto" },
      opacity: muted ? 0.55 : 1,
      boxShadow: large ? `0 6px 20px ${alpha(color, 0.2)}` : "none",
      ...sx,
    }}
  >
    <Typography
      variant={large ? "subtitle1" : compact ? "caption" : "subtitle2"}
      sx={{ fontWeight: 800, lineHeight: 1.2 }}
    >
      {name}
    </Typography>
    {role ? (
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontSize: large ? "0.75rem" : "0.68rem",
          mt: 0.2,
        }}
      >
        {role}
      </Typography>
    ) : null}
    {metric ? <MetricBar value={metric} color={color} /> : null}
  </Paper>
);

const SectionTitle = ({ children, color }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 800,
      color,
      letterSpacing: 0.6,
      display: "block",
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const WorkflowBox = ({
  title,
  items,
  owner,
  ownerColor,
  mutedOwner,
  accent = COLORS.estimates,
  variant = "estimates",
}) => {
  const borderColor =
    variant === "orders"
      ? alpha(COLORS.orders, 0.45)
      : variant === "samples"
        ? alpha(COLORS.samples, 0.4)
        : alpha(COLORS.estimates, 0.5);
  const tint =
    variant === "samples"
      ? alpha(COLORS.samples, 0.1)
      : variant === "orders"
        ? alpha(COLORS.orders, 0.08)
        : alpha(COLORS.estimates, 0.1);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: "2px solid",
        borderColor,
        bgcolor: tint,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5, mb: owner ? 0 : 0 }}>
        {items.map((item) => (
          <Typography
            key={item}
            component="li"
            variant="caption"
            sx={{ lineHeight: 1.4 }}
          >
            {item}
          </Typography>
        ))}
      </Box>
      {owner ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 0.75,
            mt: 1,
          }}
        >
          <PersonBox
            name={owner}
            color={ownerColor}
            borderColor={ownerColor}
            bgcolor={alpha(ownerColor, 0.15)}
            compact
          />
          {mutedOwner ? (
            <PersonBox
              name={mutedOwner}
              color="#9e9e9e"
              borderColor="#bdbdbd"
              compact
              muted
            />
          ) : null}
        </Box>
      ) : null}
    </Paper>
  );
};

const TeamGroup = ({ label, dashed, children, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1,
      borderRadius: 2,
      border: dashed ? "2px dashed" : "1px solid",
      borderColor: dashed ? alpha(accent, 0.55) : "divider",
      bgcolor: dashed ? alpha(accent, 0.04) : "background.paper",
      flex: { xs: "1 1 100%", sm: "1 1 100px" },
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontWeight: 800,
        letterSpacing: 1,
        color: dashed ? accent : "text.secondary",
        display: "block",
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>{children}</Box>
  </Paper>
);

const FlowArrow = ({ label, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.35,
      px: 0.75,
      py: 0.5,
      borderRadius: 1.5,
      bgcolor: alpha(color, 0.1),
      border: "1px dashed",
      borderColor: alpha(color, 0.45),
      whiteSpace: "nowrap",
    }}
  >
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color, fontSize: "0.65rem" }}
    >
      {label}
    </Typography>
    <EastOutlinedIcon sx={{ fontSize: 14, color }} />
  </Box>
);

/** Fila alineada: Irene | flecha | Borja */
const AlignedRow = ({ left, arrowLabel, arrowColor, right }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "minmax(0, 1fr) 44px minmax(0, 1fr)",
      },
      gap: { xs: 1, md: 1.25 },
      alignItems: "center",
    }}
  >
    <Box sx={{ minWidth: 0 }}>{left}</Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
      <FlowArrow label={arrowLabel} color={arrowColor} />
    </Box>
    <Box sx={{ minWidth: 0 }}>{right}</Box>
    <Box
      sx={{
        display: { xs: "flex", md: "none" },
        gridColumn: "1 / -1",
        justifyContent: "center",
      }}
    >
      <FlowArrow label={arrowLabel} color={arrowColor} />
    </Box>
  </Box>
);

const OrganigramaChart = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const isDark = theme.palette.mode === "dark";
  const connector = isDark ? alpha("#fff", 0.22) : alpha("#000", 0.12);
  const chartList = (key) =>
    t(`organigrama.chart.${key}`, { returnObjects: true }) || [];

  const ireneShellSx = {
    p: 1.5,
    borderRadius: 2.5,
    border: "2px solid",
    borderColor: alpha(COLORS.irene, 0.28),
    bgcolor: alpha(COLORS.irene, 0.03),
  };

  const borjaShellSx = {
    p: 1.5,
    borderRadius: 2.5,
    border: "2px solid",
    borderColor: alpha(COLORS.borja, 0.3),
    bgcolor: alpha(COLORS.borja, 0.04),
  };

  return (
    <Box sx={{ overflowX: isMobile ? "visible" : "auto", pb: 1 }}>
      <Box
        sx={{
          minWidth: isMobile ? 0 : 720,
          maxWidth: 980,
          mx: "auto",
          width: "100%",
          px: { xs: 0, md: 1 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          {/* Dirección ejecutiva */}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.85),
              background: isDark
                ? `linear-gradient(145deg, ${alpha(COLORS.miguel, 0.12)} 0%, ${alpha(COLORS.irene, 0.14)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                : "linear-gradient(145deg, #fff8e6 0%, #e8f5e9 48%, #fafafa 100%)",
              boxShadow: isDark
                ? "0 8px 28px rgba(0,0,0,0.25)"
                : "0 10px 36px rgba(15,23,42,0.06)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${COLORS.miguel}, ${COLORS.irene})`,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: 1.2,
                color: "text.secondary",
                display: "block",
                textAlign: "center",
                mb: 1.5,
              }}
            >
              {t("organigrama.chart.founderCco")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 2, md: 4 },
                flexWrap: "wrap",
              }}
            >
              {/*  <PersonBox
              name="MIGUEL"
              role="EC"
              color={COLORS.miguel}
              borderColor={COLORS.miguel}
              bgcolor={alpha(COLORS.miguel, 0.16)}
              large
            />
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                width: 48,
                height: 2,
                bgcolor: connector,
              }}
            /> */}
              <PersonBox
                name="IRENE"
                role={t("organigrama.chart.ccoFounder")}
                /* metric="14.900" */
                color={COLORS.irene}
                borderColor={COLORS.irene}
                bgcolor={alpha(COLORS.irene, 0.12)}
                large
              />
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.85),
              background: isDark
                ? `linear-gradient(145deg, ${alpha(COLORS.miguel, 0.12)} 0%, ${alpha(COLORS.irene, 0.14)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                : "linear-gradient(145deg, #fff8e6 0%, #e8f5e9 48%, #fafafa 100%)",
              boxShadow: isDark
                ? "0 8px 28px rgba(0,0,0,0.25)"
                : "0 10px 36px rgba(15,23,42,0.06)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${COLORS.miguel}, ${COLORS.irene})`,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: 1.2,
                color: "text.secondary",
                display: "block",
                textAlign: "center",
                mb: 1.5,
              }}
            >
              {t("organigrama.chart.executiveLeadership")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 2, md: 4 },
                flexWrap: "wrap",
              }}
            >
              <PersonBox
                name="MIGUEL"
                role="EC"
                color={COLORS.miguel}
                borderColor={COLORS.miguel}
                bgcolor={alpha(COLORS.miguel, 0.16)}
                large
              />
              {/* <Box
              sx={{
                display: { xs: "none", sm: "block" },
                width: 48,
                height: 2,
                bgcolor: connector,
              }}
            />
            <PersonBox
              name="IRENE"
              role="CPCPO"
             
              color={COLORS.irene}
              borderColor={COLORS.irene}
              bgcolor={alpha(COLORS.irene, 0.12)}
              large
            /> */}
            </Box>
          </Paper>
        </Box>
        {/* Cabeceras de columnas */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 44px 1fr" },
            gap: { xs: 0.5, md: 1.25 },
            mb: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: COLORS.irene,
              textAlign: { md: "center" },
            }}
          >
            {t("organigrama.chart.colCommercial")}
          </Typography>
          <Box sx={{ display: { xs: "none", md: "block" } }} />

          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: COLORS.borja,
              textAlign: { md: "center" },
            }}
          >
            {t("organigrama.chart.colOperations")}
          </Typography>
        </Box>

        {/* Filas alineadas Irene ↔ Borja */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Fila 1: Ventas ↔ Estimates */}
          <AlignedRow
            arrowLabel={t("organigrama.chart.arrowBudgets")}
            arrowColor={COLORS.estimates}
            left={
              <Paper
                elevation={0}
                sx={{
                  ...ireneShellSx,
                  borderRadius: { xs: 2.5, md: "14px" },
                  /* borderBottom: { md: "none" }, */
                }}
              >
                <SectionTitle color={COLORS.irene}>
                  {t("organigrama.chart.salesTeam")}
                </SectionTitle>
                {/* <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Lydia y Mikael
                </Typography> */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <PersonBox
                    name="IRENE"
                    metric="14.900"
                    color={COLORS.irene}
                    borderColor={COLORS.irene}
                    bgcolor={alpha(COLORS.irene, 0.12)}
                    large
                  />

                  <PersonBox
                    name="LYDIA"
                    metric="3.500"
                    color={COLORS.lydia}
                    borderColor={COLORS.lydia}
                    bgcolor={alpha(COLORS.lydia, 0.1)}
                  />
                  <PersonBox
                    name="MIKAEL"
                    metric="500"
                    color={COLORS.mikael}
                    borderColor="#bdbdbd"
                    bgcolor={alpha(COLORS.mikael, 0.06)}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 1,
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  {t("organigrama.chart.salesCaption")}
                </Typography>
              </Paper>
            }
            right={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <PersonBox
                    name="BORJA"
                    role="COFO"
                    color={COLORS.borja}
                    borderColor={COLORS.borja}
                    bgcolor={alpha(COLORS.borja, 0.12)}
                  />
                </Box>
                <WorkflowBox
                  title={t("organigrama.chart.estimates")}
                  items={chartList("estimatesItems")}
                  owner="Imma"
                  ownerColor={COLORS.imma}
                  mutedOwner="Guillermo"
                  variant="estimates"
                />
              </Box>
            }
          />

          {/* Fila 2: Diseño ↔ Seguimiento + Production */}
          <AlignedRow
            arrowLabel={t("organigrama.chart.arrowSamples")}
            arrowColor={COLORS.samples}
            left={
              <Paper
                elevation={0}
                sx={{
                  ...ireneShellSx,
                  borderRadius: { xs: 2.5, md: "14px" },
                }}
              >
                <SectionTitle color={COLORS.agustina}>
                  {t("organigrama.chart.designTeam")}
                </SectionTitle>

                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                  <PersonBox
                    name="AGUSTINA"
                    role={t("organigrama.chart.headDesigners")}
                    color={COLORS.agustina}
                    borderColor={COLORS.agustina}
                    bgcolor={alpha(COLORS.agustina, 0.1)}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <TeamGroup label="NML" dashed accent={COLORS.lydia}>
                    <PersonBox
                      name="CHIARA"
                      color="#424242"
                      borderColor="#bdbdbd"
                      compact
                    />
                    <PersonBox
                      name="GEMMA"
                      color={COLORS.gemma}
                      borderColor={COLORS.gemma}
                      bgcolor={alpha(COLORS.gemma, 0.12)}
                      compact
                    />
                  </TeamGroup>
                  <TeamGroup label="SDS" dashed accent={COLORS.agustina}>
                    {["ANA", "ALEJANDRA", "ALEX"].map((n) => (
                      <PersonBox
                        key={n}
                        name={n}
                        color="#424242"
                        borderColor="#bdbdbd"
                        compact
                      />
                    ))}
                  </TeamGroup>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: alpha(COLORS.samples, 0.08),
                    border: "1px solid",
                    borderColor: alpha(COLORS.samples, 0.25),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.samples,
                      display: "block",
                      mb: 0.35,
                    }}
                  >
                    {t("organigrama.chart.designSamples")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.45 }}
                  >
                    {t("organigrama.chart.designSamplesDetail")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <TeamGroup label="NML | SDS" dashed accent={COLORS.lydia}>
                    <PersonBox
                      name="NINA"
                      color={COLORS.gemma}
                      borderColor={COLORS.gemma}
                      bgcolor={alpha(COLORS.gemma, 0.12)}
                      compact
                    />
                  </TeamGroup>
                </Box>
              </Paper>
            }
            right={
              <Paper
                elevation={0}
                sx={{
                  ...borjaShellSx,
                  borderRadius: { xs: 2.5, md: "14px" },
                  mt: { xs: 0, md: 2 },
                }}
              >
                <SectionTitle color={COLORS.borja}>
                  {t("organigrama.chart.opsTeam")}
                </SectionTitle>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 1.25,
                    width: "100%",
                  }}
                >
                  <WorkflowBox
                    title={t("organigrama.chart.sampleTracking")}
                    items={chartList("sampleTrackingItems")}
                    owner="Guillermo"
                    ownerColor={COLORS.guillermo}
                    mutedOwner="Imma"
                    accent={COLORS.samples}
                    variant="samples"
                  />
                  <WorkflowBox
                    title={t("organigrama.chart.ordersProduction")}
                    items={chartList("ordersProductionItems")}
                    owner="Guillermo"
                    ownerColor={COLORS.guillermo}
                    mutedOwner="Imma"
                    accent={COLORS.orders}
                    variant="orders"
                  />
                </Box>
              </Paper>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default OrganigramaChart;
